import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, API, formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { formatIDR } from "../lib/constants";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import { ArrowLeft, Loader2, Eye, Download, Pencil, Trash2, Plus } from "lucide-react";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend } from "recharts";

const tooltipStyle = { borderRadius: "8px", border: "1px solid #e2e8f0", fontSize: "12px" };

export default function AnnualReport() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [data, setData] = useState(null);
  const [editSettings, setEditSettings] = useState(false);
  const [settingsForm, setSettingsForm] = useState({ total_penghimpunan_5y: "", total_pentasyarufan_5y: "", periode: "" });
  const [reportForm, setReportForm] = useState({ year: String(new Date().getFullYear()), title: "" });
  const [reportFile, setReportFile] = useState(null);
  const [reportThumb, setReportThumb] = useState(null);
  const [saving, setSaving] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null);

  const fetchData = useCallback(async () => {
    try {
      const { data } = await api.get("/public/annual-report");
      setData(data);
      setSettingsForm({
        total_penghimpunan_5y: String(data.settings.total_penghimpunan_5y || ""),
        total_pentasyarufan_5y: String(data.settings.total_pentasyarufan_5y || ""),
        periode: data.settings.periode || "",
      });
    } catch (e) {
      toast.error(formatApiError(e));
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleSaveSettings = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.put("/annual-settings", {
        total_penghimpunan_5y: Number(settingsForm.total_penghimpunan_5y) || 0,
        total_pentasyarufan_5y: Number(settingsForm.total_pentasyarufan_5y) || 0,
        periode: settingsForm.periode,
      });
      toast.success("Pengaturan tersimpan");
      setEditSettings(false);
      fetchData();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const handleAddReport = async (e) => {
    e.preventDefault();
    if (!reportForm.title || !reportFile || !reportThumb) {
      toast.error("Lengkapi judul, file laporan (PDF), dan foto thumbnail");
      return;
    }
    setSaving(true);
    const fd = new FormData();
    fd.append("year", reportForm.year);
    fd.append("title", reportForm.title);
    fd.append("file", reportFile);
    fd.append("thumbnail", reportThumb);
    try {
      await api.post("/annual-reports", fd);
      toast.success("Laporan tahunan ditambahkan");
      setReportForm({ year: String(new Date().getFullYear()), title: "" });
      setReportFile(null);
      setReportThumb(null);
      fetchData();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteReport = async (r) => {
    if (confirmDelete !== r.report_id) {
      setConfirmDelete(r.report_id);
      setTimeout(() => setConfirmDelete(null), 3000);
      return;
    }
    try {
      await api.delete(`/annual-reports/${r.report_id}`);
      toast.success("Laporan dihapus");
      setConfirmDelete(null);
      fetchData();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const s = data?.settings || {};

  return (
    <div className="min-h-screen bg-slate-50" data-testid="annual-report-page">
      <header className="border-b border-slate-200 bg-white/90 backdrop-blur">
        <div className="mx-auto flex max-w-5xl items-center gap-3 px-4 py-3">
          <Link to="/"><img src="/logo-lazisnu.png" alt="Logo LAZISNU Dawe" className="h-10 w-10 rounded-lg bg-white object-contain p-0.5" /></Link>
          <div className="flex-1" />
          <Link to="/">
            <Button variant="outline" size="sm" data-testid="annual-back-button">
              <ArrowLeft className="mr-1.5 h-4 w-4" /> Kembali
            </Button>
          </Link>
        </div>
      </header>

      <main className="mx-auto max-w-5xl px-4 py-10 space-y-10">
        <div className="text-center">
          <h1 className="font-heading text-4xl sm:text-5xl font-bold tracking-tight text-emerald-900" data-testid="annual-title">Annual Report</h1>
          <p className="mx-auto mt-3 max-w-2xl text-sm sm:text-base text-slate-500">
            Laporan tahunan NU Care - LAZISNU sebagai wujud transparansi dan akuntabilitas dalam pengelolaan dana zakat, infak, dan sedekah
          </p>
        </div>

        {!data ? (
          <div className="flex justify-center py-16"><Loader2 className="h-8 w-8 animate-spin text-emerald-800" /></div>
        ) : (
          <>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <Card data-testid="annual-total-penghimpunan">
                <CardContent className="p-6 text-center">
                  <p className="text-xs font-semibold tracking-[0.05em] uppercase text-slate-500">Total Penghimpunan 5 Tahun</p>
                  <p className="mt-2 text-2xl font-bold font-heading text-emerald-800">{formatIDR(s.total_penghimpunan_5y)}</p>
                </CardContent>
              </Card>
              <Card data-testid="annual-total-pentasyarufan">
                <CardContent className="p-6 text-center">
                  <p className="text-xs font-semibold tracking-[0.05em] uppercase text-slate-500">Total Pentasyarufan 5 Tahun</p>
                  <p className="mt-2 text-2xl font-bold font-heading text-yellow-600">{formatIDR(s.total_pentasyarufan_5y)}</p>
                </CardContent>
              </Card>
              <Card data-testid="annual-periode">
                <CardContent className="p-6 text-center">
                  <p className="text-xs font-semibold tracking-[0.05em] uppercase text-slate-500">Periode Laporan</p>
                  <p className="mt-2 text-2xl font-bold font-heading text-slate-800">{s.periode || "-"}</p>
                </CardContent>
              </Card>
            </div>

            {isAdmin && (
              <div className="text-center">
                <Button variant="outline" size="sm" data-testid="annual-edit-settings-button" onClick={() => setEditSettings(!editSettings)}>
                  <Pencil className="mr-1.5 h-3.5 w-3.5" /> {editSettings ? "Tutup Form" : "Edit Data Ikhtisar"}
                </Button>
                {editSettings && (
                  <form onSubmit={handleSaveSettings} className="mx-auto mt-4 grid max-w-2xl grid-cols-1 md:grid-cols-4 gap-3 rounded-xl border border-slate-200 bg-white p-4 text-left" data-testid="annual-settings-form">
                    <div className="space-y-1.5">
                      <Label className="text-xs">Total Penghimpunan (Rp)</Label>
                      <Input type="number" data-testid="annual-settings-penghimpunan" value={settingsForm.total_penghimpunan_5y} onChange={(e) => setSettingsForm({ ...settingsForm, total_penghimpunan_5y: e.target.value })} />
                    </div>
                    <div className="space-y-1.5">
                      <Label className="text-xs">Total Pentasyarufan (Rp)</Label>
                      <Input type="number" data-testid="annual-settings-pentasyarufan" value={settingsForm.total_pentasyarufan_5y} onChange={(e) => setSettingsForm({ ...settingsForm, total_pentasyarufan_5y: e.target.value })} />
                    </div>
                    <div className="space-y-1.5">
                      <Label className="text-xs">Periode (cth: 2021-2025)</Label>
                      <Input data-testid="annual-settings-periode" value={settingsForm.periode} onChange={(e) => setSettingsForm({ ...settingsForm, periode: e.target.value })} />
                    </div>
                    <div className="flex items-end">
                      <Button type="submit" data-testid="annual-settings-save" disabled={saving} className="w-full bg-emerald-800 hover:bg-emerald-700 text-white">
                        {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Simpan"}
                      </Button>
                    </div>
                  </form>
                )}
              </div>
            )}

            <Card data-testid="annual-chart">
              <CardContent className="p-6">
                <h2 className="font-heading text-lg font-semibold text-slate-800 mb-4">Grafik Pertumbuhan Penghimpunan & Pentasyarufan Dana ZIS</h2>
                {(data.yearly || []).length === 0 ? (
                  <p className="py-10 text-center text-sm text-slate-400">Belum ada data transaksi</p>
                ) : (
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart data={data.yearly} margin={{ left: 8, right: 8 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                      <XAxis dataKey="tahun" tick={{ fontSize: 12 }} stroke="#94a3b8" />
                      <YAxis tick={{ fontSize: 11 }} stroke="#94a3b8" tickFormatter={(v) => `${(v / 1000000).toFixed(0)}jt`} />
                      <Tooltip contentStyle={tooltipStyle} formatter={(v) => formatIDR(v)} />
                      <Legend />
                      <Bar dataKey="penerimaan" name="Penghimpunan" fill="#065f46" radius={[4, 4, 0, 0]} />
                      <Bar dataKey="pentasyarufan" name="Pentasyarufan" fill="#eab308" radius={[4, 4, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                )}
              </CardContent>
            </Card>

            <div>
              <h2 className="font-heading text-2xl font-bold text-slate-800">Daftar Laporan Tahunan</h2>
              <p className="text-sm text-slate-500 mt-1">Unduh dan pelajari laporan kinerja LAZISNU Dawe</p>

              {isAdmin && (
                <form onSubmit={handleAddReport} className="mt-4 grid grid-cols-1 md:grid-cols-5 gap-3 rounded-xl border border-slate-200 bg-white p-4" data-testid="annual-add-form">
                  <div className="space-y-1.5">
                    <Label className="text-xs">Tahun</Label>
                    <Input data-testid="annual-add-year" value={reportForm.year} onChange={(e) => setReportForm({ ...reportForm, year: e.target.value })} required />
                  </div>
                  <div className="space-y-1.5 md:col-span-2">
                    <Label className="text-xs">Judul (cth: Laporan Pengelolaan Dana ZIS & DSKL NU Care-LAZISNU Tahun 2025)</Label>
                    <Input data-testid="annual-add-title" value={reportForm.title} onChange={(e) => setReportForm({ ...reportForm, title: e.target.value })} required />
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-xs">File PDF</Label>
                    <Input type="file" accept="application/pdf" data-testid="annual-add-file" onChange={(e) => setReportFile(e.target.files?.[0] || null)} required />
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-xs">Foto Thumbnail</Label>
                    <div className="flex gap-2">
                      <Input type="file" accept="image/*" data-testid="annual-add-thumb" onChange={(e) => setReportThumb(e.target.files?.[0] || null)} required />
                      <Button type="submit" data-testid="annual-add-submit" disabled={saving} className="bg-emerald-800 hover:bg-emerald-700 text-white shrink-0">
                        {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
                      </Button>
                    </div>
                  </div>
                </form>
              )}

              {(data.reports || []).length === 0 ? (
                <p className="mt-6 rounded-xl border border-dashed border-slate-300 py-10 text-center text-sm text-slate-400" data-testid="annual-empty">
                  Belum ada laporan tahunan yang diunggah
                </p>
              ) : (
                <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  {data.reports.map((r) => (
                    <Card key={r.report_id} className="overflow-hidden transition-all duration-200 hover:-translate-y-[2px] hover:shadow-md" data-testid={`annual-report-${r.report_id}`}>
                      <img src={`${API}/files/${r.thumb_path}`} alt={r.title} className="aspect-video w-full object-cover" />
                      <CardContent className="p-4">
                        <p className="text-sm font-semibold text-slate-800 leading-snug">{r.title}</p>
                        <div className="mt-3 flex items-center gap-2">
                          <a href={`${API}/files/${r.file_path}`} target="_blank" rel="noreferrer" data-testid={`annual-preview-${r.report_id}`}>
                            <Button variant="outline" size="sm"><Eye className="mr-1.5 h-3.5 w-3.5" /> Preview</Button>
                          </a>
                          <a href={`${API}/files/${r.file_path}`} download={r.file_name || r.title} data-testid={`annual-download-${r.report_id}`}>
                            <Button variant="outline" size="sm"><Download className="mr-1.5 h-3.5 w-3.5" /> Unduh</Button>
                          </a>
                          {isAdmin && (
                            <button
                              data-testid={`annual-delete-${r.report_id}`}
                              onClick={() => handleDeleteReport(r)}
                              className={`ml-auto rounded-md p-1.5 transition-colors duration-200 ${confirmDelete === r.report_id ? "bg-red-600 text-white" : "text-slate-400 hover:bg-red-50 hover:text-red-600"}`}
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          )}
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </div>
          </>
        )}
      </main>
    </div>
  );
}
